import { Component, EventEmitter, Input, Output, SimpleChanges } from "@angular/core";
import { Action, ColumnDto, DisplayStyleConfig, TableDataType } from "../../../models/table-meta.interface";
import { NgClass, NgIf, NgStyle } from "@angular/common";
import { SvgIconComponent } from "../../svg-icon/svg-icon.component";
import { TooltipDirective } from "../../../directives/tooltip.directive";
import { TosTableService } from "../../../services/tos-table.service";
import { convertEpochToReadableISTDateTime } from "../../../utils/date-time-utils";
import { ExpressionEvaluator } from "../../../services/expression-evaluator.service";
import { CommonUtils } from "../../../utils/common-utils";
import defaultStyleConfig from "./default-style.json";
import { A, N } from "@angular/cdk/keycodes";

interface DefaultStyles {
  [key: string]: Partial<DisplayStyleConfig>;
}
export const CLICKABLE_TD_EVENT_KEY = "clickable-td-event";

@Component({
  selector: "app-tos-table-td",
  standalone: true,
  imports: [
    NgClass,
    SvgIconComponent,
    NgIf,
    TooltipDirective,
    NgStyle
  ],
  templateUrl: "./tos-table-td.component.html",
  styleUrl: "./tos-table-td.component.scss"
})
export class TosTableTdComponent {
  @Input() value: any;
  @Input() rowData?: any;
  @Input() column!: ColumnDto;
  @Input() effectiveConfig!: DisplayStyleConfig | null;

  @Output() inlineAction = new EventEmitter<{ actionName: string, record: any }>();

  resolvedValue!: string;

  private defaultStyles: DefaultStyles = defaultStyleConfig;


  constructor(private tosTableService: TosTableService, private evaluator: ExpressionEvaluator) {
  }

  ngOnInit() {
    this.applyDefaultStyles();
    this.effectiveConfig = this.getEffectiveConfig();
    this.resolvedValue = this.formatCellValue(this.value, this.column.dataType);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["value"]) {
      this.resolvedValue = this.formatCellValue(this.value, this.column.dataType);
      this.effectiveConfig = this.getEffectiveConfig();
    }
  }

  protected readonly TableDataType = TableDataType;

  handleClickableElement() {
    if (this.column.dataType == "CLICKABLE")
      this.inlineAction.emit({ actionName: CLICKABLE_TD_EVENT_KEY, record: this.rowData });
  }

  convertEpochToDateTime(value: any) {
    return convertEpochToReadableISTDateTime(value);
  }

  applyDefaultStyles() {
    if (!this.column?.styleConfig?.type) return;

    const type = this.column.styleConfig.type;
    const defaults = this.getDefaultStyles(type);

    this.column.styleConfig.icon = this.column.styleConfig.icon || defaults.icon;
    this.column.styleConfig.iconPosition = this.column.styleConfig.iconPosition || defaults.iconPosition;
    if (defaults.styleProperties && !this.column.styleConfig.styleProperties) {
      this.column.styleConfig.styleProperties = { ...defaults.styleProperties };
    }

    if (defaults.conditions && !this.column.styleConfig.conditions) {
      this.column.styleConfig.conditions = [...defaults.conditions];
    }

    if (defaults.iconStyleProperties && !this.column.styleConfig.iconStyleProperties) {
      this.column.styleConfig.iconStyleProperties = { ...defaults.iconStyleProperties };
    }

  }

  getDefaultStyles(type: string): Partial<DisplayStyleConfig> {
    return type in this.defaultStyles ? this.defaultStyles[type] : {};
  }

  getLabelValue(item: any): any {
    const labelKey = this.getLabelKey();
    return item?.[labelKey] ?? item;
  }

  getLabelKey() {
    return this.column?.styleConfig?.multiTagConfig?.labelKey || "name";
  }

  getEffectiveConfig(): DisplayStyleConfig | null {
    if (!this.column?.styleConfig) return null;

    const baseConfig = this.column.styleConfig;

    if (!baseConfig.conditions?.length) return baseConfig;

    const matchedCondition = baseConfig.conditions.find(condition =>
      this.evaluator.evaluate(condition.expression, {
        value: this.value,
        row: this.rowData
      })
    );

    if (!matchedCondition) return baseConfig;

    const mergedStyleProperties = {
      ...baseConfig.styleProperties,
      ...matchedCondition.styleProperties
    };

    const mergedIconStyleProperties = {
      ...baseConfig.iconStyleProperties,
      ...matchedCondition.iconStyleProperties
    };

    return {
      ...baseConfig,
      ...matchedCondition,
      styleProperties: Object.keys(mergedStyleProperties).length ? mergedStyleProperties : undefined,
      iconStyleProperties: Object.keys(mergedIconStyleProperties).length ? mergedIconStyleProperties : undefined
    };
  }

  evaluateButtonVisibility(btn: Action): boolean {
    if ((btn.conditions ?? []).length === 0) return true;
    const matchedCondition = btn.conditions?.find(condition =>
      this.evaluator.evaluate(condition.expression, {
        value: this.value,
        row: this.rowData
      })
    );
    return matchedCondition ? true : false;
  }

  handleButtonClick(btn?: Action): void {
    if (!btn) return;
    this.inlineAction.emit({ actionName: btn.actionName, record: this.rowData });
  }

  getCellStyles() {
    if (!this.effectiveConfig) return {};
    return this.effectiveConfig?.styleProperties;
  }

  getIconStyles() {
    if (!this.effectiveConfig || !this.effectiveConfig?.iconStyleProperties) return {};
    return this.effectiveConfig?.iconStyleProperties;
  }

  getTagStyles(index: number) {
    if (!this.effectiveConfig) return {};
    return {
      "color": this.effectiveConfig.multiTagConfig?.orderedValueStyles?.[index].color || this.effectiveConfig.multiTagConfig?.color,
      "background-color": this.effectiveConfig.multiTagConfig?.orderedValueStyles?.[index].backgroundColor || this.effectiveConfig.multiTagConfig?.backgroundColor
    };
  }

  getTagItems(): any[] {
    return this.value ? this.value.slice(0, Math.min(this.value.length, 3)) : [];
  }

  getTooltipData(): any[] {
    return this.value.slice(3);
  }

  getCellClasses(): string {
    if (!this.effectiveConfig) return "";

    return [
      `display-${this.effectiveConfig.type || "text"}`,
      //this.effectiveConfig.icon && 'icon_cover',
      this.effectiveConfig.iconPosition && `icon-${this.effectiveConfig.iconPosition}`
    ].filter(Boolean).join(" ");
  }

  formatCellValue(value: any, dataType: string) {
    if (this.column?.valueMap) {
      if (typeof this.value === "boolean") {
        return this.column.valueMap[String(this.value)] || String(this.value);
      }

      return this.column.valueMap[this.value] || this.value;
    }


    const formattedValue = CommonUtils.formatData(value, dataType);
    return formattedValue || this.column?.defaultValue || "";
  }

  protected readonly N = N;
  protected readonly A = A;
}
